import classNames from 'classnames'
import type {
  SegmentedLabeledOption as RcSegmentedLabeledOption,
  SegmentedProps as RCSegmentedProps,
  SegmentedRawOption,
} from 'rc-segmented'
import RcSegmented from 'rc-segmented'
import * as React from 'react'

import './index.less'

export type SizeType = 'small' | 'middle' | 'large' | undefined
const SizeContext = React.createContext<SizeType>(undefined)

export type { SegmentedValue } from 'rc-segmented'

interface SegmentedLabeledOptionWithoutIcon extends RcSegmentedLabeledOption {
  label: RcSegmentedLabeledOption['label']
}

interface SegmentedLabeledOptionWithIcon extends Omit<RcSegmentedLabeledOption, 'label'> {
  label?: RcSegmentedLabeledOption['label']
  /** Set icon for Segmented item */
  icon: React.ReactNode
}

function isSegmentedLabeledOptionWithIcon(
  option: SegmentedRawOption | SegmentedLabeledOptionWithIcon | SegmentedLabeledOptionWithoutIcon
): option is SegmentedLabeledOptionWithIcon {
  return typeof option === 'object' && !!(option as SegmentedLabeledOptionWithIcon)?.icon
}

export type SegmentedLabeledOption =
  | SegmentedLabeledOptionWithIcon
  | SegmentedLabeledOptionWithoutIcon

export interface SegmentedProps extends Omit<RCSegmentedProps, 'size' | 'options'> {
  rootClassName?: string
  options: (SegmentedRawOption | SegmentedLabeledOption)[]
  block?: boolean
  size?: SizeType
}

const Segmented = React.forwardRef<HTMLDivElement, SegmentedProps>((props, ref) => {
  const {
    prefixCls: customizePrefixCls,
    className,
    rootClassName,
    block,
    options = [],
    size: customSize = 'middle',
    style,
    ...restProps
  } = props

  const prefixCls = 'tntd-segmented'

  // ===================== Size =====================
  const size = React.useContext(SizeContext)
  const mergedSize = customSize || size

  // syntactic sugar to support `icon` for Segmented Item
  const extendedOptions = React.useMemo<RCSegmentedProps['options']>(
    () =>
      options.map((option) => {
        if (isSegmentedLabeledOptionWithIcon(option)) {
          const { icon, label, ...restOption } = option
          return {
            ...restOption,
            label: (
              <>
                <span className={`${prefixCls}-item-icon`}>{icon}</span>
                {label && <span>{label}</span>}
              </>
            ),
          }
        }
        return option
      }),
    [options, prefixCls]
  )

  const cls = classNames(className, rootClassName, {
    [`${prefixCls}-block`]: block,
    [`${prefixCls}-sm`]: mergedSize === 'small',
    [`${prefixCls}-lg`]: mergedSize === 'large',
  })

  const mergedStyle: React.CSSProperties = { ...style }

  return (
    <RcSegmented
      {...restProps}
      className={cls}
      style={mergedStyle}
      options={extendedOptions}
      //   options={options}
      ref={ref}
      rev=""
      prefixCls={prefixCls}
    />
  )
})

if (process.env.NODE_ENV !== 'production') {
  Segmented.displayName = 'Segmented'
}

export default Segmented
